import React, { useState, useEffect, useCallback } from "react";
import { Plus, Minus, X, ShoppingCart, ArrowRight } from "react-feather";
import { FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { base_api_url } from "../../../environment.jsx";

const CartSidePanel = ({ isOpen, onClose, restauId, Table, onCartUpdate }) => {
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();

  const [recaporder, setRecaporder] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cartQuantity, setCartQuantity] = useState(0);
  const [reference, setReference] = useState(null);

  const devise =
    JSON.parse(localStorage.getItem("restaurant"))?.Restaurant?.devise ??
    JSON.parse(localStorage.getItem("restaurant"))?.restaurant?.devise;

  // Fetch order details whenever the panel opens
  const fetchOrderDetails = useCallback(async () => {
    setLoading(true);
    try {
      const client = JSON.parse(sessionStorage.getItem("client"));
      if (!client?.reference) {
        setRecaporder([]);
        setCartQuantity(0);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${base_api_url}api/commande/${client.reference}`);
      if (response.status === 200 || response.status === 201) {
        setReference(response.data.reference);

        const transformedData =
          response.data.details?.map((detail) => {
            const article = detail.article?.[0];
            return {
              id_details: detail.id_details,
              id_article: article?.id_article,
              title: article?.nom || "Article inconnu",
              underTitle: article?.description || "",
              prix: detail.prix_unitaire,
              quantity: detail.quantite,
              prix_total: detail.prix_total,
              image_url:
                article?.image_url && !article?.image_url.startsWith("data:image")
                  ? `${base_api_url}api/recuperer/${article.image_url}`
                  : article?.image_url,
            };
          }) || [];

        setRecaporder(transformedData);

        const totalQty =
          response.data.details?.reduce((t, d) => t + (d.quantite || 0), 0) || 0;
        setCartQuantity(totalQty);

        // Sync localStorage for specific-instruction
        const totalPrice = transformedData.reduce((t, i) => t + i.prix * i.quantity, 0);
        localStorage.setItem("total", totalPrice);
        localStorage.setItem("quantite", totalQty);

        if (onCartUpdate) onCartUpdate(totalQty);
      }
    } catch (err) {
      console.error("CartSidePanel fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [onCartUpdate]);

  useEffect(() => {
    if (isOpen) fetchOrderDetails();
  }, [isOpen, fetchOrderDetails]);

  // ---- Quantity change (optimistic) ----
  const handleQuantityChange = async (detailId, newQuantity) => {
    if (newQuantity < 1) return;
    const originalData = [...recaporder];
    const originalQty = cartQuantity;

    const oldItem = recaporder.find((i) => i.id_details === detailId);
    const diff = newQuantity - (oldItem?.quantity || 0);

    const updated = recaporder.map((i) =>
      i.id_details === detailId ? { ...i, quantity: newQuantity, prix_total: i.prix * newQuantity } : i
    );
    setRecaporder(updated);
    const newQty = cartQuantity + diff;
    setCartQuantity(newQty);
    localStorage.setItem("quantite", newQty);
    localStorage.setItem("total", updated.reduce((t, i) => t + i.prix * i.quantity, 0));
    if (onCartUpdate) onCartUpdate(newQty);

    try {
      await axios.put(`${base_api_url}api/detailscmd/${detailId}/${newQuantity}`);
    } catch (e) {
      setRecaporder(originalData);
      setCartQuantity(originalQty);
      if (onCartUpdate) onCartUpdate(originalQty);
      MySwal.fire({
        icon: "error",
        text: "Impossible de modifier la quantité.",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        customClass: { popup: "pixel-toast" },
      });
    }
  };

  // ---- Delete ----
  const handleDelete = async (id_article) => {
    try {
      const res = await axios.put(`${base_api_url}api/rmdetailscmd/${id_article}/${reference}`);
      if (res.status === 200) {
        MySwal.fire({
          icon: "success",
          text: "Article supprimé !",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 2000,
          customClass: { popup: "pixel-toast" },
        });
        fetchOrderDetails();
      }
    } catch (e) {
      MySwal.fire({
        icon: "error",
        text: "Impossible de supprimer l'article.",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        customClass: { popup: "pixel-toast" },
      });
    }
  };

  const calculateTotal = () => recaporder.reduce((t, i) => t + i.prix * i.quantity, 0);

  const handleCheckout = () => {
    const total = calculateTotal();
    localStorage.setItem("total", total);
    localStorage.setItem("quantite", cartQuantity);
    onClose();
    navigate(`/specific-instruction?r=${restauId}&t=${Table}`);
  };

  // ---- Styles ----
  const overlayStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    zIndex: 9998,
    opacity: isOpen ? 1 : 0,
    pointerEvents: isOpen ? "auto" : "none",
    transition: "opacity 0.3s ease",
  };

  const panelStyle = {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    height: "100dvh", // Modern browsers: excludes toolbar
    width: "min(420px, 100vw)",
    backgroundColor: "#fff",
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    transform: isOpen ? "translateX(0)" : "translateX(100%)",
    transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
    borderRadius: window.innerWidth < 420 ? "0" : "16px 0 0 16px", // No radius if full width
  };

  return (
    <>
      {/* Overlay */}
      <div style={overlayStyle} onClick={onClose} />

      {/* Panel */}
      <div style={panelStyle}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 20px 16px",
            borderBottom: "1px solid #f0f0f0",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ShoppingCart size={22} color="#E7592C" />
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#001b44" }}>
              Mon Panier
            </h2>
            {cartQuantity > 0 && (
              <span
                style={{
                  backgroundColor: "#E7592C",
                  color: "#fff",
                  borderRadius: "50%",
                  width: 22,
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {cartQuantity}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#f5f5f7",
              border: "none",
              borderRadius: "50%",
              width: 36,
              height: 36,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#666",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 20px" }}>
          {loading ? (
            <div
              style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}
            >
              <div className="spinner-border text-primary" role="status" style={{ width: 36, height: 36 }}>
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : recaporder.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "60%",
                gap: 16,
                color: "#aaa",
              }}
            >
              <ShoppingCart size={56} strokeWidth={1} />
              <p style={{ fontSize: 16, margin: 0, fontWeight: 500 }}>Votre panier est vide</p>
              <p style={{ fontSize: 13, margin: 0, color: "#bbb" }}>
                Ajoutez des articles pour commencer
              </p>
            </div>
          ) : (
            <div style={{ paddingBottom: 8 }}>
              {recaporder.map((item, index) => (
                <div
                  key={item.id_details || index}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "16px 0",
                    borderBottom: index < recaporder.length - 1 ? "1px solid #f5f5f7" : "none",
                  }}
                >
                  {/* Image */}
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: 10,
                      backgroundImage: item.image_url ? `url(${item.image_url})` : "none",
                      backgroundColor: "#f0f0f0",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      flexShrink: 0,
                    }}
                  />

                  {/* Center: name + unit price + stepper */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: "0 0 2px 0",
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#111",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}>
                      {item.title}
                    </p>
                    <p style={{ margin: "0 0 10px 0", fontSize: 12, color: "#999" }}>
                      {item.prix} {devise} / unité
                    </p>

                    {/* Qty stepper */}
                    <div style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 10,
                      backgroundColor: "#f5f5f7",
                      borderRadius: 20,
                      padding: "5px 10px",
                    }}>
                      <button
                        onClick={() => {
                          if (item.quantity === 1) handleDelete(item.id_article);
                          else handleQuantityChange(item.id_details, item.quantity - 1);
                        }}
                        style={{
                          width: 26, height: 26, borderRadius: "50%", border: "none",
                          backgroundColor: "#fff", cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.12)", color: "#555",
                        }}
                      >
                        <Minus size={12} />
                      </button>

                      <span style={{ fontSize: 14, fontWeight: 600, minWidth: 18, textAlign: "center", color: "#111" }}>
                        {item.quantity}
                      </span>

                      <button
                        onClick={() => handleQuantityChange(item.id_details, item.quantity + 1)}
                        style={{
                          width: 26, height: 26, borderRadius: "50%", border: "none",
                          backgroundColor: "#fff", cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.12)", color: "#555",
                        }}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>

                  {/* Right: total price + trash */}
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    height: 64,
                    flexShrink: 0,
                  }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>
                      {(item.prix * item.quantity).toLocaleString("fr-FR", {
                        minimumFractionDigits: 2, maximumFractionDigits: 2,
                      })}{" "}{devise}
                    </span>
                    <button
                      onClick={() => handleDelete(item.id_article)}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "#ccc", padding: 2,
                        display: "flex", alignItems: "center",
                        transition: "color 0.2s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "#E7592C")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "#ccc")}
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {recaporder.length > 0 && (
          <div
            style={{
              padding: "16px 20px 28px",
              borderTop: "1px solid #f0f0f0",
              flexShrink: 0,
              backgroundColor: "#fff",
            }}
          >
            {/* Total */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 600, color: "#666" }}>Total</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: "#001b44" }}>
                {calculateTotal().toLocaleString("fr-FR", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}{" "}
                {devise}
              </span>
            </div>

            {/* Checkout button */}
            <button
              onClick={handleCheckout}
              style={{
                width: "100%",
                padding: "15px 20px",
                backgroundColor: "#E7592C",
                color: "#fff",
                border: "none",
                borderRadius: 14,
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 6px 20px rgba(231,89,44,0.35)",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(231,89,44,0.45)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(231,89,44,0.35)";
              }}
            >
              <span>Passer au paiement</span>
              <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

CartSidePanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  restauId: PropTypes.string,
  Table: PropTypes.string,
  onCartUpdate: PropTypes.func,
};

export default CartSidePanel;
